import baseAPIService from '../../../../services/baseapiservice';

interface ReportDataRequest {
    rpt_id: number;
    filters?: Array<{ type: string; value: any }>;
    ulb_id?: number;
    /**
     * Optional UDA override for $$uda_id$$ in the report SQL. Sent by screens that
     * carry their own UDA picker (Employee Management reports). The server honours it
     * only for state/regional users — a UDA-scoped user stays pinned to their own UDA.
     */
    udaId?: number | null;
    page?: number;
    pageSize?: number;
    searchQuery?: string;
    searchableColumns?: string[];
    useServerPagination?: boolean;
}

interface PaginationInfo {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
}

interface ReportMetadataResponse {
    fltrdata: any[];
    clmnsdata: any[];
    config_json_txt?: any;
    rpt_nm?: string;
    qry_id?: number;
    /**
     * True when this report's SQL carries the uda_id placeholder and can therefore
     * answer the header's UDA picker. False = the picker is shown inert.
     */
    uda_aware_in?: boolean;
}

/** A row of the UDA master, as the report header's UDA picker needs it. */
export interface ReportUdaOption {
    uda_id: number;
    uda_cd?: string;
    uda_nm: string;
}

interface ReportDataResponse {
    tabledata: any[];
    pagination?: PaginationInfo;
    useServerPagination?: boolean;
}

/** Session cache for the UDA master — see fetchUdas(). */
let udaCache: ReportUdaOption[] | null = null;
let udaPromise: Promise<ReportUdaOption[]> | null = null;

class ReportsServices {

    async getReportList(urlPath?: string) {
        // Use apufidc-auth for APUFIDC users (fetches from apufidc_enc_db schema)
        const authType = localStorage.getItem('auth_type');
        const authEndpoint = authType === 'crda' ? 'crda-auth' : authType === 'apufidc' ? 'apufidc-auth' : 'erp-auth';
        const rte = urlPath ? `${authEndpoint}/menu/reports?url=${encodeURIComponent(urlPath)}` : `${authEndpoint}/menu/reports`;
        try {
            const res = await baseAPIService.get(rte);
            if (res.status == 200) {
                return res.data;
            }
            throw new Error('Failed to fetch report list');
        } catch (error) {
            console.error('Error fetching report list:', error);
            throw error;
        }
    }

    /**
     * UDA options for the report header's picker. Sourced from the UDA master
     * (ds_dtcp.ds_uda_lst_t via erp-admin/uda), so a UDA with no rows in this report is
     * still selectable and reads as empty rather than missing. Cached for the session —
     * the list is fixed, and every report mount would otherwise re-ask.
     */
    async fetchUdas(): Promise<ReportUdaOption[]> {
        if (udaCache) return udaCache;
        if (!udaPromise) {
            udaPromise = (async () => {
                const res = await baseAPIService.get('erp-admin/uda');
                if (res.data?.status !== 200) {
                    throw new Error(res.data?.message || 'Failed to fetch UDAs');
                }
                const list: ReportUdaOption[] = (res.data?.data || [])
                    .filter((u: any) => u?.uda_id != null)
                    .map((u: any) => ({
                        uda_id: Number(u.uda_id),
                        uda_cd: u.uda_cd,
                        uda_nm: u.uda_nm || u.uda_cd || String(u.uda_id),
                    }))
                    .sort((a: ReportUdaOption, b: ReportUdaOption) => a.uda_nm.localeCompare(b.uda_nm));
                udaCache = list;
                return list;
            })().catch((err) => { udaPromise = null; throw err; });
        }
        return udaPromise;
    }

    async fetchReportMetadata(rptId: number): Promise<ReportMetadataResponse> {
        try {
            const res = await baseAPIService.get(`reports/${rptId}/metadata`);
            if (res.status == 200) {
                return res.data;
            }
            throw new Error('Failed to fetch report metadata');
        } catch (error) {
            console.error('Error fetching report metadata:', error);
            throw error;
        }
    }

    async fetchReportData(params: ReportDataRequest): Promise<ReportDataResponse> {
        try {
            const res = await baseAPIService.post('reports/data', params);
            if (res.status == 200) {
                return res.data;
            }
            throw new Error('Failed to fetch report data');
        } catch (error) {
            console.error('Error fetching report data:', error);
            throw error;
        }
    }

    async saveGridConfiguration(postData: any) {
        try {
            const res = await baseAPIService.post('reports/grid-configurations/save', postData);
            if (res.status == 200) {
                return res.data;
            }
            throw new Error('Failed to save grid configuration');
        } catch (error) {
            console.error('Error saving grid configuration:', error);
            throw error;
        }
    }
}

export default new ReportsServices();
